'use client'

import { useState, useMemo, useEffect } from 'react'
import MoodSelector from '@/components/MoodSelector'
import ProjectList from '@/components/projects/ProjectList'
import AddProjectDrawer from '@/components/projects/AddProjectDrawer'
import EditProjectDrawer from '@/components/projects/EditProjectDrawer'
import { MOOD_PROFILES, scoreProjects } from '@/lib/scoring'
import { Project } from '@/lib/types'
import { useAuth } from '@/contexts/AuthContext'
import { useProjects } from '@/contexts/ProjectsContext'
import AuthForm from '@/components/auth/AuthForm'

// Sample data removed - now using real Firebase data

export default function HomePage() {
  const { user, loading: authLoading } = useAuth()
  const { projects, loading: projectsLoading, createProject, updateProject, deleteProject, markWorkedOn, recordAppOpen, error } = useProjects()
  // Frozen to 'productive' (40/40/20) for the 21-day decay window — it gives
  // the decay term the most authority, making a NO-GO decisive. See
  // docs/2026-08-03-decay-thesis-preregistration.md §7. Restore 'balanced' as
  // the default when the window closes.
  const [currentMood, setCurrentMood] = useState('productive')
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false)
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [previewProject, setPreviewProject] = useState<Partial<Project> | null>(null)
  
  const currentMoodProfile = MOOD_PROFILES[currentMood]

  // Merge preview project with existing projects for real-time ranking
  const projectsWithPreview = useMemo(() => {
    if (!previewProject || !previewProject.title?.trim()) {
      return projects
    }

    // Create complete preview project with required fields
    const completePreviewProject: Project = {
      id: previewProject.id || 'preview-temp',
      title: previewProject.title || '',
      description: previewProject.description || '',
      status: previewProject.status || 'Current',
      importance: previewProject.importance ?? 2,
      urgency: previewProject.urgency ?? 2,
      scope: previewProject.scope ?? 2,
      enjoyment: previewProject.enjoyment ?? 3,
      createdAt: new Date(),
      updatedAt: new Date(),
      userId: 'temp'
    }

    return [...projects, completePreviewProject]
  }, [projects, previewProject])

  // Score projects including preview
  const scoredProjects = useMemo(() => 
    scoreProjects(projectsWithPreview, currentMoodProfile)
  , [projectsWithPreview, currentMoodProfile])

  const handleAddProject = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    try {
      await createProject(projectData)
      setIsAddDrawerOpen(false)
    } catch (error) {
      console.error('Failed to create project:', error)
      // Error is handled by the context
    }
  }

  const handleEditProject = (project: Project) => {
    setEditingProject(project)
    setIsEditDrawerOpen(true)
  }

  const handleUpdateProject = async (projectId: string, updates: Partial<Omit<Project, 'id' | 'createdAt' | 'userId'>>) => {
    try {
      await updateProject(projectId, updates)
      setIsEditDrawerOpen(false)
      setEditingProject(null)
    } catch (error) {
      console.error('Failed to update project:', error)
      // Error is handled by the context
    }
  }

  const handleMarkWorked = async (projectId: string) => {
    try {
      await markWorkedOn(projectId)
    } catch (error) {
      console.error('Failed to record work:', error)
      // Error is handled by the context
    }
  }

  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await deleteProject(projectId)
      } catch (error) {
        console.error('Failed to delete project:', error)
        // Error is handled by the context
      }
    }
  }

  // Usage-survival instrumentation for the 21-day decay window (§6.3 of the
  // pre-registration). Fires once per authenticated session; the write is
  // idempotent per local day and swallows its own errors.
  useEffect(() => {
    if (user) {
      recordAppOpen(user.uid)
    }
    // recordAppOpen is stable for the life of the provider; depending on it
    // would re-fire this on every context re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Find preview project in scored list for special styling
  const previewProjectId = previewProject?.id
  
  // Show loading state while authenticating or loading projects
  if (authLoading || projectsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {authLoading ? 'Setting up your workspace...' : 'Loading your projects...'}
          </p>
        </div>
      </div>
    )
  }

  // Show authentication form if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          {/* App Logo/Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Eisenhowerish
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Mood-responsive project prioritization
            </p>
          </div>
          
          {/* Authentication Form */}
          <AuthForm />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Error
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          What should you work on right now?
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
          Your priorities change with your mood. Select how you feel right now, and we will show you the perfect project to tackle.
        </p>
      </div>

      {/* Mood Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            How are you feeling right now?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Your mood affects what projects feel most appealing and doable
          </p>
          <div className="flex justify-center">
            <MoodSelector 
              currentMood={currentMood} 
              onMoodChange={setCurrentMood}
            />
          </div>
        </div>
      </div>

      {/* Projects List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <ProjectList 
          projects={scoredProjects} 
          currentMoodLabel={currentMoodProfile.label}
          previewProjectId={previewProjectId}
          onEditProject={handleEditProject}
          onDeleteProject={handleDeleteProject}
          onMarkWorked={handleMarkWorked}
        />
      </div>

      {/* Call to Action */}
      <div className="text-center pt-4">
        <button 
          onClick={() => setIsAddDrawerOpen(true)}
          className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
        >
          Add New Project
        </button>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
          Add your own projects to get personalized recommendations
        </p>
      </div>

      {/* Add Project Drawer */}
      <AddProjectDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
        onProjectAdded={handleAddProject}
        onPreview={setPreviewProject}
      />

      {/* Edit Project Drawer */}
      <EditProjectDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false)
          setEditingProject(null)
        }}
        project={editingProject}
        onProjectUpdated={handleUpdateProject}
      />
    </div>
  )
}